import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const PhotoEvidenceUpload = ({ requestId }) => {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const fileInputRef = useRef(null);
  const MAX_PHOTOS = 10;
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files || []);
    const validFiles = [];

    for (const file of files) {
      if (photos?.length + validFiles?.length >= MAX_PHOTOS) {
        alert(`Maximum ${MAX_PHOTOS} photos allowed`);
        break;
      }

      if (file?.size > MAX_FILE_SIZE) {
        alert(`${file?.name} is too large. Maximum size is 5MB`);
        continue;
      }

      if (!file?.type?.startsWith('image/')) {
        alert(`${file?.name} is not an image file`);
        continue;
      }

      validFiles?.push(file);
    }

    if (validFiles?.length > 0) {
      setPhotos(prev => [...prev, ...validFiles?.map(file => ({
        file,
        preview: URL.createObjectURL(file),
        status: 'pending'
      }))]);
    }

    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = (index) => {
    setPhotos(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated?.[index]?.preview);
      return updated?.filter((_, i) => i !== index);
    });
  };

  const handleUpload = async () => {
    if (photos?.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];

    for (let i = 0; i < photos?.length; i++) {
      const photo = photos?.[i];
      if (photo?.status === 'uploaded') {
        uploadedUrls?.push(photo?.url);
        continue;
      }

      try {
        const fileName = `${Date.now()}-${photo?.file?.name}`;
        const filePath = `evidence/${requestId}/${fileName}`;

        const { error: uploadError } = await supabase?.storage
          ?.from('service-request-photos')
          ?.upload(filePath, photo?.file);

        if (uploadError) throw uploadError;

        uploadedUrls?.push(filePath);
        setPhotos(prev => {
          const updated = [...prev];
          updated[i] = { ...updated?.[i], status: 'uploaded', url: filePath };
          return updated;
        });
      } catch (error) {
        console.error('Upload error:', error);
        setPhotos(prev => {
          const updated = [...prev];
          updated[i] = { ...updated?.[i], status: 'error' };
          return updated;
        });
      }
    }

    setUploading(false);
    alert(`Successfully uploaded ${uploadedUrls?.length} photo(s)`);
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-smooth"
      >
        <div className="flex items-center gap-2">
          <Icon name="Camera" size={20} color="var(--color-primary)" strokeWidth={2} />
          <h2 className="text-lg font-semibold text-foreground">Photo Evidence Upload</h2>
          {photos?.length > 0 && (
            <span className="bg-primary text-primary-foreground text-xs font-bold px-2 py-0.5 rounded-full">
              {photos?.length}
            </span>
          )}
        </div>
        <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={20} strokeWidth={2} />
      </button>

      {expanded && (
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Upload up to {MAX_PHOTOS} high-resolution photos (5MB each)
            </p>
            <span className="text-sm font-medium text-foreground">
              {photos?.length}/{MAX_PHOTOS}
            </span>
          </div>

          {photos?.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {photos?.map((photo, index) => (
                <div key={index} className="relative group">
                  <img
                    src={photo?.preview}
                    alt={`Evidence ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg border border-border"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemovePhoto(index)}
                    className="absolute top-2 right-2 bg-error text-error-foreground rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                  >
                    <Icon name="X" size={16} />
                  </button>
                  <div className="absolute bottom-2 left-2 flex items-center gap-1">
                    {photo?.status === 'uploaded' && (
                      <span className="bg-success text-success-foreground text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Icon name="Check" size={12} />
                        Uploaded
                      </span>
                    )}
                    {photo?.status === 'error' && (
                      <span className="bg-error text-error-foreground text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Icon name="AlertCircle" size={12} />
                        Failed
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            {photos?.length < MAX_PHOTOS && (
              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  iconName="Upload"
                  iconPosition="left"
                  onClick={() => fileInputRef?.current?.click()}
                  fullWidth
                >
                  {photos?.length === 0 ? 'Add Photos' : 'Add More'}
                </Button>
              </div>
            )}
            {photos?.length > 0 && (
              <Button
                variant="default"
                size="lg"
                iconName="CloudUpload"
                iconPosition="left"
                onClick={handleUpload}
                loading={uploading}
                disabled={photos?.every(p => p?.status === 'uploaded')}
                className="flex-1"
              >
                Upload Photos
              </Button>
            )}
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">
                Upload photos showing repair progress, parts used, completed work, or any issues encountered during service.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoEvidenceUpload;